import { Agent } from '../agent';
import { gpt5ReasoningSettingsRequired, isGpt5Default } from '../default-model';
import type { Model, ModelSettings } from '../model';
import type { AgentToolUseTracker } from './tool-use-tracker';

const hasGpt5OnlySettings = (settings?: ModelSettings): boolean => {
  const providerData = settings?.providerData as
    | {
        reasoning?: unknown;
        text?: { verbosity?: unknown };
        reasoning_effort?: unknown;
      }
    | undefined;
  return Boolean(
    providerData?.reasoning ||
      providerData?.text?.verbosity ||
      (providerData as { reasoning_effort?: unknown } | undefined)
        ?.reasoning_effort,
  );
};

const NESTED_MODEL_SETTINGS_MERGE_KEYS = ['reasoning', 'text'] as const;

function isPlainObjectLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function mergeNestedObjectMap(
  targetRecord: Record<string, unknown>,
  inheritedRecord: Record<string, unknown>,
  overrideRecord: Record<string, unknown>,
  key: string,
): void {
  if (
    isPlainObjectLike(inheritedRecord[key]) &&
    isPlainObjectLike(overrideRecord[key])
  ) {
    targetRecord[key] = {
      ...inheritedRecord[key],
      ...overrideRecord[key],
    };
  }
}

/**
 * Resolves the effective model for the next turn by giving precedence to the agent-specific
 * configuration when present, otherwise falling back to the runner-level default.
 */
export function selectModel(
  agentModel: string | Model,
  runConfigModel: string | Model | undefined,
): string | Model {
  if (
    (typeof agentModel === 'string' &&
      agentModel !== Agent.DEFAULT_MODEL_PLACEHOLDER) ||
    agentModel
  ) {
    return agentModel;
  }
  return runConfigModel ?? agentModel ?? Agent.DEFAULT_MODEL_PLACEHOLDER;
}

export function mergeModelSettings(
  inheritedModelSettings?: ModelSettings,
  overrideModelSettings?: ModelSettings,
): ModelSettings {
  const mergedModelSettings: ModelSettings = {
    ...inheritedModelSettings,
    ...overrideModelSettings,
  };

  if (!inheritedModelSettings || !overrideModelSettings) {
    return mergedModelSettings;
  }

  const inheritedModelSettingsRecord = inheritedModelSettings as Record<
    string,
    unknown
  >;
  const overrideModelSettingsRecord = overrideModelSettings as Record<
    string,
    unknown
  >;
  const mergedModelSettingsRecord = mergedModelSettings as Record<
    string,
    unknown
  >;

  for (const key of NESTED_MODEL_SETTINGS_MERGE_KEYS) {
    mergeNestedObjectMap(
      mergedModelSettingsRecord,
      inheritedModelSettingsRecord,
      overrideModelSettingsRecord,
      key,
    );
  }
  mergeRetrySettings(
    mergedModelSettingsRecord,
    inheritedModelSettingsRecord,
    overrideModelSettingsRecord,
  );

  return mergedModelSettings;
}

function mergeRetrySettings(
  targetRecord: Record<string, unknown>,
  inheritedRecord: Record<string, unknown>,
  overrideRecord: Record<string, unknown>,
): void {
  if (
    !isPlainObjectLike(inheritedRecord.retry) ||
    !isPlainObjectLike(overrideRecord.retry)
  ) {
    return;
  }

  const inheritedRetry = inheritedRecord.retry;
  const overrideRetry = overrideRecord.retry;
  const mergedRetry = {
    ...inheritedRetry,
    ...overrideRetry,
  };

  if (
    isPlainObjectLike(inheritedRetry.backoff) &&
    isPlainObjectLike(overrideRetry.backoff)
  ) {
    mergedRetry.backoff = {
      ...inheritedRetry.backoff,
      ...overrideRetry.backoff,
    };
  }

  targetRecord.retry = mergedRetry;
}

/**
 * When the default model is a GPT-5 variant, agents may carry GPT-5-specific providerData
 * (e.g., reasoning effort, text verbosity). If a run resolves to a non-GPT-5 model and the
 * agent relied on the default model (i.e., no explicit model set), these GPT-5-only settings
 * are incompatible and should be stripped to avoid runtime errors.
 */
export function adjustModelSettingsForNonGPT5RunnerModel(
  explictlyModelSet: boolean,
  agentModelSettings: ModelSettings,
  runnerModel: string | Model,
  modelSettings: ModelSettings,
  resolvedModelName?: string,
): ModelSettings {
  const modelName =
    resolvedModelName ??
    (typeof runnerModel === 'string'
      ? runnerModel
      : ((runnerModel as { model?: string; name?: string } | undefined)
          ?.model ?? (runnerModel as { name?: string } | undefined)?.name));
  const isNonGpt5RunnerModel =
    typeof modelName === 'string'
      ? !gpt5ReasoningSettingsRequired(modelName)
      : true;
  const hasGpt5Defaults =
    hasGpt5OnlySettings(agentModelSettings) ||
    hasGpt5OnlySettings(modelSettings);

  if (
    isGpt5Default() &&
    explictlyModelSet &&
    isNonGpt5RunnerModel &&
    hasGpt5Defaults
  ) {
    return stripGpt5OnlySettings(modelSettings);
  }
  return modelSettings;
}

function stripGpt5OnlySettings(modelSettings: ModelSettings): ModelSettings {
  const copiedProviderData = modelSettings.providerData
    ? { ...modelSettings.providerData }
    : undefined;

  if (copiedProviderData) {
    if (
      copiedProviderData.text &&
      typeof copiedProviderData.text === 'object'
    ) {
      copiedProviderData.text = { ...copiedProviderData.text };
      delete (copiedProviderData.text as any).verbosity;
    }
    delete (copiedProviderData as any).reasoning;
    delete (copiedProviderData as any).reasoning_effort;
  }

  const copiedModelSettings: ModelSettings = {
    ...modelSettings,
    providerData: copiedProviderData,
  };
  if (modelSettings.retry) {
    copiedModelSettings.retry = { ...modelSettings.retry };
    if (modelSettings.retry.backoff) {
      copiedModelSettings.retry.backoff = { ...modelSettings.retry.backoff };
    }
  }
  if (modelSettings.reasoning) {
    copiedModelSettings.reasoning = { ...modelSettings.reasoning };
  }

  delete copiedModelSettings.providerData?.reasoning;
  delete (copiedModelSettings.providerData as any)?.text?.verbosity;
  delete (copiedModelSettings.providerData as any)?.reasoning_effort;
  if (copiedModelSettings.reasoning) {
    delete copiedModelSettings.reasoning.effort;
    delete copiedModelSettings.reasoning.summary;
  }
  return copiedModelSettings;
}

/**
 * Resets the tool choice when the agent is configured to prefer a fresh tool selection after
 * any tool usage. This prevents the provider from reusing stale tool hints across turns.
 */
export function maybeResetToolChoice(
  agent: Agent<any, any>,
  toolUseTracker: AgentToolUseTracker,
  modelSettings: ModelSettings,
) {
  if (
    agent.resetToolChoice &&
    toolUseTracker.hasUsedTools(agent) &&
    modelSettings.toolChoice !== 'none'
  ) {
    return { ...modelSettings, toolChoice: undefined };
  }
  return modelSettings;
}
